/**
 * One photo per city in the collection, and who took it.
 *
 * Every photo comes from Wikimedia Commons under a licence that allows
 * commercial use — CC BY, CC BY-SA or CC0 — and each of those licences requires
 * crediting the author, naming the licence and saying the image was changed
 * (here: cropped and resized). That is what the credits page renders from this
 * table, so a photo cannot be added without its credit.
 *
 * Regenerated by web/scripts/fetch-city-photos.py, which reads the chosen file
 * names from web/scripts/city-photos.json.
 */

export type CityPhoto = {
  city: string;
  src: string;
  author: string;
  licence: string;
  licenceUrl: string;
  /** The file page on Commons, where the full image and its terms live. */
  source: string;
};

export const CITY_PHOTOS: Record<string, CityPhoto> = {
  "rabat": {
    city: "Rabat",
    src: "/assets/cities/rabat.jpg",
    author: "Remi Jouan",
    licence: "CC BY-SA 3.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/3.0/",
    source: "https://commons.wikimedia.org/wiki/File:Rabat_Tour_Hassan.jpg",
  },
  "casablanca": {
    city: "Casablanca",
    src: "/assets/cities/casablanca.jpg",
    author: "Milamber's portfolio",
    licence: "CC BY 2.0",
    licenceUrl: "https://creativecommons.org/licenses/by/2.0",
    source: "https://commons.wikimedia.org/wiki/File:Sunshine_on_mosque_Hassan_II_in_Casablanca,_Morocco_-_Flickr_-_Milamber%27s_portfolio.jpg",
  },
  "marrakech": {
    city: "Marrakech",
    src: "/assets/cities/marrakech.jpg",
    author: "Rol1000",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Marokko0112_(retouched).jpg",
  },
  "fes": {
    city: "Fès",
    src: "/assets/cities/fes.jpg",
    author: "Momed.salhi",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:University_karaouiyine_fes.jpg",
  },
  "tanger": {
    city: "Tanger",
    src: "/assets/cities/tanger.jpg",
    author: "Ifni95",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Tanger_cor.jpg",
  },
  "ifrane": {
    city: "Ifrane",
    src: "/assets/cities/ifrane.jpg",
    author: "Amina Lahbabi",
    licence: "CC BY-SA 3.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    source: "https://commons.wikimedia.org/wiki/File:Al_Akhawayn_Campus.jpg",
  },
  "benguerir": {
    city: "Benguerir",
    src: "/assets/cities/benguerir.jpg",
    author: "You belong with mee",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Ben_guerir.png",
  },
  "kenitra": {
    city: "Kénitra",
    src: "/assets/cities/kenitra.jpg",
    author: "Fauve",
    licence: "CC BY 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Angle_de_la_route_nationale_1_et_l%E2%80%99avenue_Mohammed_Diouri.jpg",
  },
  "meknes": {
    city: "Meknès",
    src: "/assets/cities/meknes.jpg",
    author: "Chris Martin from Decatur, Georgia, USA",
    licence: "CC BY-SA 2.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    source: "https://commons.wikimedia.org/wiki/File:Bab_Mansour_Gate.jpg",
  },
  "agadir": {
    city: "Agadir",
    src: "/assets/cities/agadir.jpg",
    author: "ZAINEB HACHAMI",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:%D9%85%D8%A7%D8%B1%D9%8A%D9%86%D8%A7_%D8%A3%D9%83%D8%A7%D8%AF%D9%8A%D8%B1.jpg",
  },
  "tetouan": {
    city: "Tétouan",
    src: "/assets/cities/tetouan.jpg",
    author: "M1abdelrahman",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Tetouan.jpg",
  },
  "oujda": {
    city: "Oujda",
    src: "/assets/cities/oujda.jpg",
    author: "Farajiibrahim",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Remparts_medina_Oujda_Brahim_FARAJI_4.jpg",
  },
  "el-jadida": {
    city: "El Jadida",
    src: "/assets/cities/el-jadida.jpg",
    author: "M.Rais",
    licence: "CC BY-SA 3.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    source: "https://commons.wikimedia.org/wiki/File:Forteresse_Jadida_01.JPG",
  },
  "sale": {
    city: "Salé",
    src: "/assets/cities/sale.jpg",
    author: "R Prazeres",
    licence: "CC BY-SA 4.0",
    licenceUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    source: "https://commons.wikimedia.org/wiki/File:Sal%C3%A9_mosque_DSCF6808.jpg",
  },
};

/**
 * "El Jadida" -> "el-jadida", "Fès" -> "fes": the same slugs the API gives
 * the cities, derived from the display name the tiles already have.
 */
export const citySlug = (name: string): string =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const photoForCity = (name: string): CityPhoto | undefined => CITY_PHOTOS[citySlug(name)];
